export type SessionType = "refreshable" | "non-refreshable";

/**
 * Cross-platform token storage adapter.
 *
 * Cognito sessions (expiresIn is set): the access token lives in-memory only
 * and the refresh token lives in an HttpOnly cookie on web, or via this
 * adapter on mobile.
 *
 * Legacy sessions (expiresIn is null): the access token never expires and
 * has no refresh mechanism — it MUST be persisted via setAccessToken so it
 * survives a page refresh.
 */
export interface TokenStorageAdapter {
  getRefreshToken(): Promise<string | null>;
  setRefreshToken(token: string | null): Promise<void>;
  getAccessToken(): Promise<string | null>;
  setAccessToken(token: string | null): Promise<void>;
  /**
   * Persisted session-type marker. Required for cold-boot hydration of
   * Cognito (refreshable) sessions: the access token is in-memory only and
   * the refresh token lives somewhere the auth client can't read directly
   * on web (HttpOnly cookie). Without this marker, `hydrate()` can't tell
   * a freshly-booted Cognito user apart from a logged-out one.
   */
  getSessionType(): Promise<SessionType | null>;
  setSessionType(type: SessionType | null): Promise<void>;
  /** Clears every key this adapter owns — access token, refresh token, AND session marker. */
  clearTokens(): Promise<void>;
}

/** Default no-op storage for environments that don't need persistence. */
export const noOpTokenStorage: TokenStorageAdapter = {
  getRefreshToken: () => Promise.resolve(null),
  setRefreshToken: () => Promise.resolve(),
  getAccessToken: () => Promise.resolve(null),
  setAccessToken: () => Promise.resolve(),
  getSessionType: () => Promise.resolve(null),
  setSessionType: () => Promise.resolve(),
  clearTokens: () => Promise.resolve(),
};
